<template>
  <!-- A row like the app's menu: icon tile, title, badge and chevron. It is
       a router link, an external link, or (without href) a button that
       emits click. -->
  <component
    :is="tag"
    v-bind="linkAttrs"
    :class="['menu-item', { 'menu-item-button': !href }]"
    v-on="href ? {} : { click: event => $emit('click', event) }"
  >
    <span :class="['menu-item-tile', `menu-item-tile--${tone}`]">
      <menu-icon :icon="iconClass" />
    </span>
    <span class="menu-item-title">{{ title }}</span>
    <badge-count :notifications="badge" class="inline" />
    <menu-icon icon="chevron" class="menu-item-chevron" />
  </component>
</template>

<script>
import MenuIcon from '../Icons/MenuIcon';
import BadgeCount from '../Icons/BadgeCount';

export default {
  name: 'MenuItem',

  components: {
    MenuIcon,
    BadgeCount
  },

  props: {
    // Omit href to render the item as a button that emits `click` instead of
    // navigating.
    href: {
      type: String,
      default: ''
    },
    iconClass: {
      type: String,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    badge: {
      type: Number,
      default: 0
    },
    external: {
      type: Boolean,
      default: false
    },
    // Icon tile colour: 'primary' (blue tile and icon) or 'neutral' (grey
    // tile, dark icon).
    tone: {
      type: String,
      default: 'neutral',
      validator: value => ['primary', 'neutral'].includes(value)
    }
  },

  computed: {
    tag() {
      if (!this.href) return 'button';
      return this.external ? 'a' : 'router-link';
    },

    linkAttrs() {
      if (!this.href) return { type: 'button' };
      return this.external ? { href: this.href } : { to: this.href };
    }
  }
};
</script>

<style lang="scss" scoped>
.menu-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  min-height: 60px;
  padding: 10px 14px;
  box-sizing: border-box;
  color: var(--Text-Primary);
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;

  @include row-divider(14px);

  // Hovering keeps the colour, makes the chevron bolder and darker, and
  // the icon tile a little bigger.
  @media (hover: hover) {
    &:hover .menu-item-chevron {
      stroke-width: 2.5px;
      opacity: 1;
    }

    &:hover .menu-item-tile {
      transform: scale(1.1);
    }
  }

  &:focus-visible {
    outline: 2px solid var(--Primary-Color);
    outline-offset: -2px;
  }
}

.menu-item-button {
  appearance: none;
  background: none;
  border: 0;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
}

.menu-item-tile {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  transition: transform 0.1s ease-out;

  // MenuIcon paints its stroke/fill with --menu-icon-color.
  &--primary {
    background-color: var(--Primary-Tint);
    --menu-icon-color: var(--Primary-Color);
  }

  &--neutral {
    background-color: var(--Surface-Subtle);
    --menu-icon-color: var(--Text-Primary);
  }
}

.menu-item-title {
  flex: 1;
  min-width: 0;
}

.menu-item-chevron {
  flex-shrink: 0;
  opacity: 0.4;
  transition: stroke-width 0.1s ease-out, opacity 0.1s ease-out;
}
</style>
